import React, { useMemo } from 'react';
import type { Mention } from '../../hooks';
import type { HyperlinkMatch } from '../../hooks/mentions/useTextParsing';

interface OverlayRendererProps {
  value: string;
  mentions: Mention[];
  hyperlinks: HyperlinkMatch[];
  onMentionHover: (
    user: { username: string; email: string; name: string },
    event: React.MouseEvent
  ) => void;
  onHyperlinkHover: (
    url: string,
    displayText: string,
    event: React.MouseEvent
  ) => void;
  onHoverLeave: () => void;
}

const OverlayRendererComponent: React.FC<OverlayRendererProps> = ({
  value,
  mentions,
  hyperlinks,
  onMentionHover,
  onHyperlinkHover,
  onHoverLeave
}) => {
  const elements = useMemo(() => {
    if (!value) return [];

    const lines = value.split('\n');

    let currentIndex = 0;
    const elements: React.ReactElement[] = [];

    const uniqueMentions = mentions
      .reduce((acc: Mention[], mention) => {
        const hasOverlap = acc.some(
          existing =>
            !(mention.end <= existing.start || mention.start >= existing.end)
        );
        if (!hasOverlap) acc.push(mention);
        return acc;
      }, [])
      .sort((a, b) => a.start - b.start);

    lines.forEach((line, lineIndex) => {
      const lineStart = currentIndex;
      const lineEnd = currentIndex + line.length;

      const lineMentions = uniqueMentions.filter(
        m =>
          (m.start >= lineStart && m.start < lineEnd) ||
          (m.end > lineStart && m.end <= lineEnd) ||
          (m.start < lineStart && m.end > lineEnd)
      );

      const lineHyperlinks = hyperlinks.filter(
        h =>
          (h.startIndex >= lineStart && h.startIndex < lineEnd) ||
          (h.endIndex > lineStart && h.endIndex <= lineEnd) ||
          (h.startIndex < lineStart && h.endIndex > lineEnd)
      );

      if (lineMentions.length === 0 && lineHyperlinks.length === 0) {
        elements.push(
          <div key={lineIndex} className="div-with-textarea-suggestions__line">
            <span className="div-with-textarea-suggestions__text">
              {line || '\u00A0'}
            </span>
          </div>
        );
      } else {
        const parts: (string | React.ReactElement)[] = [];
        let lastLineIndex = 0;

        const annotations: Array<{
          type: 'mention' | 'hyperlink';
          localStart: number;
          localEnd: number;
          data: Mention | HyperlinkMatch;
        }> = [];

        lineMentions.forEach(mention =>
          annotations.push({
            type: 'mention',
            localStart: Math.max(0, mention.start - lineStart),
            localEnd: Math.min(line.length, mention.end - lineStart),
            data: mention
          })
        );

        lineHyperlinks.forEach(hyperlink =>
          annotations.push({
            type: 'hyperlink',
            localStart: Math.max(0, hyperlink.startIndex - lineStart),
            localEnd: Math.min(line.length, hyperlink.endIndex - lineStart),
            data: hyperlink
          })
        );

        const sorted = annotations.sort((a, b) => a.localStart - b.localStart);

        sorted.forEach((annotation, annotationIndex) => {
          if (annotation.localStart > lastLineIndex) {
            parts.push(line.substring(lastLineIndex, annotation.localStart));
          }

          if (annotation.type === 'mention') {
            const m = annotation.data as Mention;
            parts.push(
              <span
                key={`mention-${m.id}-${lineIndex}-${annotationIndex}`}
                className="div-with-textarea-suggestions__mention"
                onMouseEnter={e =>
                  onMentionHover(
                    {
                      username: m.user.username,
                      email: m.user.email,
                      name: m.user.name
                    },
                    e
                  )
                }
                onMouseLeave={onHoverLeave}
              >
                {line.substring(annotation.localStart, annotation.localEnd)}
              </span>
            );
          } else {
            const h = annotation.data as HyperlinkMatch;
            parts.push(
              <a
                key={`hyperlink-${lineIndex}-${annotationIndex}`}
                href={h.url}
                className="div-with-textarea-suggestions__hyperlink"
                target="_blank"
                rel="noopener noreferrer"
                title={h.url}
                onMouseEnter={e => onHyperlinkHover(h.url, h.displayText, e)}
                onMouseLeave={onHoverLeave}
              >
                {line.substring(annotation.localStart, annotation.localEnd)}
              </a>
            );
          }

          lastLineIndex = annotation.localEnd;
        });

        if (lastLineIndex < line.length) {
          parts.push(line.substring(lastLineIndex));
        }

        elements.push(
          <div key={lineIndex} className="div-with-textarea-suggestions__line">
            {parts.length > 0 ? (
              parts.map((part, idx) =>
                typeof part === 'string' ? (
                  <span
                    key={idx}
                    className="div-with-textarea-suggestions__text"
                  >
                    {part}
                  </span>
                ) : (
                  part
                )
              )
            ) : (
              <span className="div-with-textarea-suggestions__text">
                \u00A0
              </span>
            )}
          </div>
        );
      }

      currentIndex = lineEnd + 1;
    });

    return elements;
  }, [
    value,
    mentions,
    hyperlinks,
    onMentionHover,
    onHoverLeave,
    onHyperlinkHover
  ]);

  return <>{elements}</>;
};

// Custom comparison function to prevent unnecessary re-renders
const arePropsEqual = (
  prevProps: OverlayRendererProps,
  nextProps: OverlayRendererProps
) => {
  // Check if value changed
  if (prevProps.value !== nextProps.value) return false;

  // Check if mentions array changed (deep comparison for mentions)
  if (prevProps.mentions.length !== nextProps.mentions.length) return false;
  for (let i = 0; i < prevProps.mentions.length; i++) {
    const prevMention = prevProps.mentions[i];
    const nextMention = nextProps.mentions[i];
    if (
      prevMention.id !== nextMention.id ||
      prevMention.start !== nextMention.start ||
      prevMention.end !== nextMention.end ||
      prevMention.user.username !== nextMention.user.username ||
      prevMention.user.email !== nextMention.user.email ||
      prevMention.user.name !== nextMention.user.name
    ) {
      return false;
    }
  }

  // Check if hyperlinks array changed (deep comparison for hyperlinks)
  if (prevProps.hyperlinks.length !== nextProps.hyperlinks.length) return false;
  for (let i = 0; i < prevProps.hyperlinks.length; i++) {
    const prevHyperlink = prevProps.hyperlinks[i];
    const nextHyperlink = nextProps.hyperlinks[i];
    if (
      prevHyperlink.url !== nextHyperlink.url ||
      prevHyperlink.displayText !== nextHyperlink.displayText ||
      prevHyperlink.startIndex !== nextHyperlink.startIndex ||
      prevHyperlink.endIndex !== nextHyperlink.endIndex
    ) {
      return false;
    }
  }

  // Callback functions should be stable from the hook, so we can skip deep comparison
  return true;
};

export const OverlayRenderer = React.memo(
  OverlayRendererComponent,
  arePropsEqual
);
